import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { apiFetch, getToken, getStoredUser, clearToken } from "../lib/api";

const NAV = [
  { label: "Overview", path: "/admin/overview", ready: true },
  { label: "Opportunities", path: "/admin/opportunities", ready: true },
  { label: "Idea History", path: "/admin/idea-history", ready: true },
  { label: "Studio", path: "/admin/studio", ready: true },
  { label: "Studio Clients", path: "/admin/studio/clients", ready: true },
  { label: "Studio Appts", path: "/admin/studio/appointments", ready: true },
  { label: "Content", path: "/admin/content", ready: true },
  { label: "Leads", path: "/admin/leads", ready: true },
  { label: "Conversations", path: "/admin/conversations", ready: false },
  { label: "Conversions", path: "/admin/conversions", ready: false },
  { label: "Revenue", path: "/admin/revenue", ready: false },
  { label: "Analytics", path: "/admin/analytics", ready: false },
  { label: "AI Discovery", path: "/admin/ai-discovery", ready: true },
  { label: "Panels", path: "/admin/panels", ready: true },
  { label: "Media", path: "/admin/media", ready: true },
  { label: "Settings", path: "/admin/settings", ready: true },
];

export default function AdminLayout() {
  const [user, setUser] = useState(getStoredUser());
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
      return;
    }
    apiFetch("/auth/me")
      .then((u) => setUser(u))
      .catch(() => {
        clearToken();
        navigate("/admin");
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="text-xs uppercase tracking-[0.3em] text-warmwhite/50">Loading cockpit…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-softgrey" data-testid="admin-layout">
      <aside className="hidden w-56 shrink-0 flex-col border-r border-ink/10 bg-ink lg:flex">
        <Link to="/admin/overview" className="border-b border-warmwhite/10 p-6" data-testid="admin-home-link">
          <img src="/images/logo.png" alt="iINSPIRE" className="h-9 w-auto bg-warmwhite px-2 py-1" />
          <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.3em] text-amber">Cockpit</p>
        </Link>
        <nav className="flex-1 space-y-1 p-4" aria-label="Admin navigation">
          {NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "bg-warmwhite/10 text-amber" : "text-warmwhite/60 hover:text-warmwhite"
                }`
              }
            >
              {item.label}
              {!item.ready && <span className="ml-2 text-[8px] text-warmwhite/35">soon</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-warmwhite/10 p-4">
          <p className="px-4 text-xs text-warmwhite/60">{user?.name || user?.email}</p>
          <p className="px-4 text-[9px] uppercase tracking-[0.2em] text-warmwhite/40">{user?.role}</p>
          <button
            onClick={() => { clearToken(); navigate("/admin"); }}
            className="mt-3 flex items-center gap-2 px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-warmwhite/50 hover:text-amber"
            data-testid="admin-logout"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Logout
          </button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between border-b border-ink/10 bg-warmwhite px-6 py-4 lg:hidden">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ink">iINSPIRE Cockpit</p>
          <button onClick={() => { clearToken(); navigate("/admin"); }} className="text-xs uppercase tracking-[0.2em] text-ink/60" data-testid="admin-logout-mobile">
            Logout
          </button>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-b border-ink/10 bg-warmwhite px-4 py-2 lg:hidden" aria-label="Admin navigation mobile">
          {NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] ${isActive ? "text-burgundy" : "text-ink/50"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-6 lg:p-10">
          <Outlet context={{ user }} />
        </div>
      </div>
    </div>
  );
}
